/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { Table } from 'react-bootstrap-v5'
import Select from 'react-select'
import { KTSVG } from '../../_metronic/helpers'
import { getRequestOptions } from '../helpers/Fetchwrapper'

export interface PurchaseOrderRow {
    idItems: number
    idProducts: number
    quantity: number
    excludingRate: number
    amount: number
    discountAmount: number
    carryingCost: number
    totalAmount: number
    rate: number
    itemName?: string
    idPoRows?: number
    [key: string]: any
}

interface SelectOption {
    label: string
    value: number
    unitName?: string
    idItems?: number
    idProducts?: number
    [key: string]: any
}

interface PurchaseSummary {
    totalAmount: number
    totalDiscountAmount: number
    totalCarryingCost: number
    totalAmountAfterDiscount: number
    vatPercentage: number
    vatAmount: number
    taxPercentage: number
    taxAmount: number
    purchaseAmount: number
    purpose: string
    idProjects?: number
    [key: string]: any
}

interface PurchaseOrderItemsSectionProps {
    purchase: PurchaseSummary
    purchaseRows: PurchaseOrderRow[]
    setPurchaseRows: (rows: PurchaseOrderRow[]) => void
    onVatChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onTaxChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    numberFormat: (value: number) => string
}

const emptyRow = (): PurchaseOrderRow => ({
    idItems: 0,
    idProducts: 0,
    quantity: 0,
    excludingRate: 0,
    amount: 0,
    discountAmount: 0,
    carryingCost: 0,
    totalAmount: 0,
    rate: 0,
})

const selectStyles = {
    control: (provided: any) => ({
        ...provided,
        height: '32px',
        minHeight: '32px',
    }),
    container: (provided: any) => ({
        ...provided,
        height: '32px',
    }),
}

const PurchaseOrderItemsSection: React.FC<PurchaseOrderItemsSectionProps> = ({
    purchase,
    purchaseRows,
    setPurchaseRows,
    onVatChange,
    onTaxChange,
    numberFormat,
}) => {
    const [options, setOptions] = useState<SelectOption[]>([])
    const isFinishedProduct = purchase.purpose === 'Finished Product'
    const nameColumnLabel = isFinishedProduct ? 'Product Name' : 'Item Name'

    useEffect(() => {
        if (isFinishedProduct) {
            if (purchase.idProjects) {
                getProducts(purchase.idProjects)
            } else {
                setOptions([])
            }
        } else {
            getItems()
        }
    }, [purchase.purpose, purchase.idProjects])

    const getItems = () => {
        fetch(process.env.REACT_APP_API_URL + 'production-items', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const items = (resp.data || []).map((item: any) => ({
                    ...item,
                    label: item.itemName,
                    value: item.idItems,
                }))
                setOptions(items)
            })
            .catch((error) => {
                console.log(error, 'catch the hoop')
            })
    }

    const getProducts = (idProjects: number) => {
        fetch(
            process.env.REACT_APP_API_URL + 'po/products?idProjects=' + idProjects,
            getRequestOptions()
        )
            .then((resp) => resp.json())
            .then((resp) => {
                const products = (resp.data || []).map((item: any) => {
                    const packingUnit = [item.packing, item.unitName].filter(Boolean).join(' ')
                    const label = item.productCode
                        ? `${item.productName}${packingUnit ? ' (' + packingUnit + ')' : ''} Code: ${item.productCode}`
                        : `${item.productName}${packingUnit ? ' (' + packingUnit + ')' : ''}`
                    return {
                        ...item,
                        label,
                        value: item.idProducts,
                        unitName: packingUnit || item.unitName || '',
                    }
                })
                setOptions(products)
            })
            .catch((error) => {
                console.log(error, 'catch the hoop')
            })
    }

    const handleItemChange = (data: any, index: number) => {
        const updatedPurchaseRows = [...purchaseRows]
        if (isFinishedProduct) {
            updatedPurchaseRows[index].idProducts = data.value
            updatedPurchaseRows[index].idItems = data.idItems || 0
            updatedPurchaseRows[index].itemName = data.label || data.productName || ''
        } else {
            updatedPurchaseRows[index].idItems = data.value
            updatedPurchaseRows[index].idProducts = 0
            updatedPurchaseRows[index].itemName = data.label || data.itemName || ''
        }
        updatedPurchaseRows[index].excludingRate = 0
        setPurchaseRows(updatedPurchaseRows)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const updatedPurchaseRows = [...purchaseRows]
        updatedPurchaseRows[index][e.target.name] = e.target.value
        setPurchaseRows(updatedPurchaseRows)
    }

    const deleteItem = (index: number) => {
        const updatedPurchaseRows = [...purchaseRows]
        if (updatedPurchaseRows && updatedPurchaseRows[index]) {
            updatedPurchaseRows.splice(index, 1)
            setPurchaseRows(updatedPurchaseRows)
        }
    }

    const addItem = () => {
        setPurchaseRows([...purchaseRows, emptyRow()])
    }

    const getSelectedOption = (row: PurchaseOrderRow) => {
        if (isFinishedProduct) {
            if (!row.idProducts) return null
            const found = options.find((item) => item.value === row.idProducts)
            return found || (row.itemName ? { label: row.itemName, value: row.idProducts } : null)
        }
        if (!row.idItems) return null
        const found = options.find((item) => item.value === row.idItems)
        return found || (row.itemName ? { label: row.itemName, value: row.idItems } : null)
    }

    const getUnitName = (row: PurchaseOrderRow) => {
        if (isFinishedProduct) {
            return options.find((item) => item.value === row.idProducts)?.unitName || ''
        }
        return options.find((item) => item.value === row.idItems)?.unitName || ''
    }

    return (
        <>
            {isFinishedProduct && !purchase.idProjects && (
                <div className="alert alert-light-warning py-2 mb-3">
                    Select a project to load products.
                </div>
            )}

            <div className="row">
                <div className="col">
                    <Table size="sm" bordered hover>
                        <thead>
                            <tr>
                                <th style={{ width: '4%' }} className="text-center">SL</th>
                                <th style={{ width: '22%' }}>{nameColumnLabel} <span className="text-danger">*</span></th>
                                <th style={{ width: '5%' }}>Unit <span className="text-danger">*</span></th>
                                <th style={{ width: '8%' }}>Rate</th>
                                <th style={{ width: '8%' }}>Quantity</th>
                                <th style={{ width: '8%' }}>Amount</th>
                                <th style={{ width: '9%' }}>Discount Amount</th>
                                <th style={{ width: '8%' }}>Carrying Cost</th>
                                <th style={{ width: '9%' }}>Total Amount</th>
                                <th style={{ width: '9%' }}>Rate Including</th>
                                <th style={{ width: '5%' }}></th>
                                <th style={{ width: '5%' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {purchaseRows.map((row, index) => (
                                <tr key={index}>
                                    <td className="text-center">{index + 1}</td>
                                    <td>
                                        <Select
                                            placeholder={isFinishedProduct ? 'Select Product' : 'Select Item'}
                                            value={getSelectedOption(row)}
                                            onChange={(selectedOption) => handleItemChange(selectedOption, index)}
                                            options={options}
                                            className="sm"
                                            menuPosition="fixed"
                                            styles={selectStyles}
                                            isDisabled={isFinishedProduct && !purchase.idProjects}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            name="unitName"
                                            value={getUnitName(row)}
                                            readOnly
                                            className="form-control form-control-sm"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            name="excludingRate"
                                            value={row.excludingRate}
                                            onChange={(event) => handleChange(event, index)}
                                            className="form-control form-control-sm"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            name="quantity"
                                            value={row.quantity}
                                            onChange={(event) => handleChange(event, index)}
                                            className="form-control form-control-sm"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            name="amount"
                                            readOnly
                                            value={row.amount}
                                            className="form-control form-control-sm"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            name="discountAmount"
                                            value={row.discountAmount}
                                            onChange={(event) => handleChange(event, index)}
                                            className="form-control form-control-sm"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            name="carryingCost"
                                            value={row.carryingCost}
                                            onChange={(event) => handleChange(event, index)}
                                            className="form-control form-control-sm"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            name="totalAmount"
                                            readOnly
                                            value={row.totalAmount}
                                            className="form-control form-control-sm"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            name="rate"
                                            readOnly
                                            value={row.rate}
                                            className="form-control form-control-sm"
                                        />
                                    </td>
                                    <td>
                                        {purchaseRows.length > 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-light-primary btn-sm px-2 py-1"
                                                onClick={() => deleteItem(index)}
                                            >
                                                <KTSVG
                                                    path="/media/svg/icons/General/Trash.svg"
                                                    className="svg-icon-5 svg-icon-danger me-0"
                                                />
                                            </button>
                                        )}
                                        {index === purchaseRows.length - 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-success btn-sm px-2 py-1"
                                                onClick={addItem}
                                            >
                                                <KTSVG
                                                    path="/media/svg/icons/Navigation/Plus.svg"
                                                    className="svg-icon-5 svg-icon-danger me-0"
                                                />{' '}
                                                Add
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td colSpan={5} className="text-end"><b>Total:</b></td>
                                <td><b>{numberFormat(purchase.totalAmount)}</b></td>
                                <td><b>{numberFormat(purchase.totalDiscountAmount)}</b></td>
                                <td><b>{numberFormat(purchase.totalCarryingCost)}</b></td>
                                <td><b>{numberFormat(purchase.totalAmountAfterDiscount)}</b></td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
            </div>

            <div className="row">
                <div className="col-6"></div>
                <div className="col-6">
                    <div className="notice bg-light-primary rounded border-primary border border-dashed h-lg-100 p-6">
                        <div className="row mt-2">
                            <div className="col-md-5"><strong>Total Amount: </strong></div>
                            <div className="col-md-4">{numberFormat(purchase.totalAmount)}</div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-md-5"><strong>Total Discount Amount: </strong></div>
                            <div className="col-md-4">{numberFormat(purchase.totalDiscountAmount)}</div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-md-5"><strong>Total Carrying Cost: </strong></div>
                            <div className="col-md-4">{numberFormat(purchase.totalCarryingCost)}</div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-md-5"><strong>VAT: </strong></div>
                            <div className="col-md-4">
                                <div className="input-group input-group-sm mb-3">
                                    <input
                                        type="number"
                                        name="vatPercentage"
                                        value={purchase.vatPercentage}
                                        onChange={onVatChange}
                                        className="form-control form-control-sm"
                                        min={0}
                                        step={'any'}
                                    />
                                    <span className="input-group-text">%</span>
                                    <input
                                        type="number"
                                        name="vatAmount"
                                        id="vatAmount"
                                        value={purchase.vatAmount}
                                        className="form-control form-control-sm"
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-md-5"><strong>TAX: </strong></div>
                            <div className="col-md-4">
                                <div className="input-group input-group-sm mb-3">
                                    <input
                                        type="number"
                                        name="taxPercentage"
                                        value={purchase.taxPercentage}
                                        onChange={onTaxChange}
                                        className="form-control form-control-sm"
                                        min={0}
                                        step={'any'}
                                    />
                                    <span className="input-group-text">%</span>
                                    <input
                                        type="number"
                                        name="taxAmount"
                                        id="taxAmount"
                                        value={purchase.taxAmount}
                                        className="form-control form-control-sm"
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-md-5"><strong>Net Amount: </strong></div>
                            <div className="col-md-4">{numberFormat(purchase.purchaseAmount)}</div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default PurchaseOrderItemsSection
